import { useEffect, useRef, useState } from 'react';
import './assets/css/app.scss';
import './assets/css/iframe.scss';
import './utils/sectionSystem.js';
import OutlineSection from './components/OutlineSection.jsx';
import OutlineBlock from './components/OutlineBlock.jsx';
import OutlineElement from './components/OutlineElement.jsx';

function App() {
	const origin = process.env.REACT_APP_HOST;
	const previewRef = useRef();
	const [inspectorMode, setInspectorMode] = useState(true);
	const [count, setCount] = useState(0);
	const [selectedSectionId, setSelectedSectionId] = useState(false);
	const [selectedBlockId, setSelectedBlockId] = useState({});
	const [selectedElementId, setSelectedElementId] = useState({});

	useEffect(() => {
		const handleKeyDown = (e) => {
			if (!(e.ctrlKey || e.metaKey)) return;
			const key = e.key.toLowerCase();
			let action = false;
			if (key === 'z') {
				action = e.shiftKey ? 'key-redo' : 'key-undo';
			} else if (key === 'i') {
				action = 'key-inspector';
			} else if (key === 's') {
				action = 'key-save';
			}

			if (action) {
				e.preventDefault();
				window.parent.postMessage({
					type: [action]
				}, '*');
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, []);

	const addSections = new Set();

	useEffect(() => {
		const handler = async (e) => {
			if (e.origin === origin || e.origin === 'https://test.smind.io' || e.origin === 'https://app.smind.io') {
				if (e.data.type.includes('add')) {
					const data = e.data.data;
					const { sectionId, blockId, html, appended, noScroll = true, index } = data;

					const exists = previewRef.current.querySelector('#' + sectionId);

					if (!exists) {
						if (addSections.has(sectionId)) {
							return;
						}
						addSections.add(sectionId);

						const div = document.createElement('div');
						div.innerHTML = html;
						const firstDiv = div.firstElementChild;

						if (!appended) {
							if (typeof index === 'number') {
								const wrappers = Array.from(
									previewRef.current.querySelectorAll('section-wrapper')
								);

								if (index >= wrappers.length) {
									previewRef.current.appendChild(firstDiv);
								} else {
									previewRef.current.insertBefore(firstDiv, wrappers[index]);
								}
							} else {
								previewRef.current.appendChild(firstDiv);
							}
						} else {
							const currentEl = document
								.getElementById(appended.sectionId)
								?.closest('section-wrapper');

							if (currentEl) {
								if (appended.position === 'below') {
									currentEl.insertAdjacentElement('afterend', firstDiv);
								} else {
									currentEl.parentNode.insertBefore(firstDiv, currentEl);
								}
							}
						}

						if (!noScroll) {
							setTimeout(() => {
								firstDiv.scrollIntoView({
									behavior: 'smooth',
									block: 'start',
									inline: 'center',
								});
							}, 500);
						}
					} else {
						const wrapper = exists.closest('section-wrapper');
						if (wrapper) {
							wrapper.refresh(html, sectionId);
						}
					}
				}

				if (e.data.type.includes('add-many')) {
					const { htmls, order } = e.data.data;
					if (!htmls || !order) return;

					const container = previewRef.current;

					const existingMap = {};
					container.querySelectorAll('[section-id]').forEach(el => {
						const sId = el.getAttribute('section-id');
						if (sId) existingMap[sId] = el;
					});

					const topPreview = container.querySelector('#top-preview');
					const btnClear = container.querySelector('.btn-clear');

					let lastNode = topPreview || null;

					order.forEach(sectionId => {
						const html = htmls[sectionId];
						const existing = existingMap[sectionId];

						if (existing) {
							if (html && typeof existing.refresh === 'function') existing.refresh(html);

							if (lastNode && lastNode.nextSibling !== existing) {
								container.insertBefore(existing, lastNode.nextSibling);
							}

							lastNode = existing;
							delete existingMap[sectionId];
						} else if (html) {
							const div = document.createElement('div');
							div.innerHTML = html.trim();
							const newSection = div.firstElementChild;

							if (!newSection) return;

							if (!newSection.id) newSection.id = `shopify-section-${sectionId}`;
							if (!newSection.getAttribute('section-id'))
								newSection.setAttribute('section-id', sectionId);

							if (lastNode && lastNode.nextSibling) {
								container.insertBefore(newSection, lastNode.nextSibling);
							} else if (lastNode) {
								container.appendChild(newSection);

								queueMicrotask(() => {
									if (typeof newSection.refresh === "function") {
										newSection.refresh(html);
									}
								});
							} else {
								container.insertBefore(newSection, btnClear || null);
							}

							lastNode = newSection;
						}
					});

					Object.values(existingMap).forEach(el => el.remove());
				}

				if (e.data.type.includes('delete')) {
					const data = e.data.data;
					const { sectionId } = data;

					const exists = previewRef.current.querySelector('#' + sectionId);
					if (exists) {
						const wrapper = exists.closest('section-wrapper');
						if (wrapper) {
							const shopifySection = wrapper.querySelector('.shopify-section');

							if (shopifySection) {
								shopifySection.dispatchEvent(new CustomEvent('shopify:section:deselect', {
									detail: {
										sectionId: sectionId,
									},
									bubbles: true,
								}));

								shopifySection.dispatchEvent(new CustomEvent('shopify:section:unload', {
									detail: {
										sectionId: sectionId,
									},
									bubbles: true,
								}));
							}

							wrapper.remove();
						}
					}
				}

				if (e.data.type.includes('delete-block')) {
					const data = e.data.data;
					const { sectionId, blockId } = data;

					const exists = previewRef.current.querySelector('#' + sectionId);
					if (exists) {
						const wrapper = exists.closest('section-wrapper');
						if (wrapper) {
							const shopifySection = wrapper.querySelector('.shopify-section');

							if (shopifySection) {
								// shopifySection.dispatchEvent(new CustomEvent('shopify:block:deselect', {
								// 	detail: {
								// 		sectionId,
								// 		blockId
								// 	},
								// 	bubbles: true,
								// }));
							}
						}
					}
				}

				if (e.data.type.includes('move')) {
					let temp = e.data.data
					const { sectionId, belowId } = temp;

					if (sectionId != belowId) {
						let appendEl = document.getElementById(belowId)
						let currentEl = document.getElementById(sectionId)

						appendEl.parentNode.insertBefore(currentEl, appendEl.nextSibling);

						if (appendEl) {
							currentEl.scrollIntoView({
								//behavior: !!smooth ? "smooth" : "instant",
								behavior: "smooth",
								block: "center",
								inline: "center",
							});
						}
					}
				}

				if (e.data.type.includes('move-start')) {
					let temp = e.data.data
					const { sectionId } = temp;

					if (sectionId) {
						const activeEl = document.getElementById(sectionId);
						if (activeEl) {
							activeEl.classList.add('move');
						}
					}
				}

				if (e.data.type.includes('move-end')) {
					let temp = e.data.data
					const { sectionId } = temp;

					const allActive = document.querySelectorAll('.section.move');
					allActive.forEach(item => {
						item.classList.remove('move');
					})

					if (sectionId) {
						const activeEl = document.getElementById(sectionId);
						if (activeEl) {
							activeEl.classList.remove('move');
						}
					}
				}

				if (e.data.type.includes('change-style')) {
					let temp = e.data.data
					const { style } = temp;

					if (style) {
						const styleEl = document.querySelector('#template-styles');
						if (styleEl) {
							styleEl.textContent = style;
						}
					}
				}

				if (e.data.type.includes('change-font')) {
					let temp = e.data.data

					const { style, remove } = temp;

					const styleOld = document.querySelector('#preview-font')
					if (styleOld) {
						styleOld.remove();
					}

					if (!remove && style !== '') {
						const head = document.head || document.getElementsByTagName('head')[0]
						const styleTag = document.createElement('style');
						styleTag.id = 'preview-font'
						head.appendChild(styleTag);
						styleTag.type = 'text/css';
						if (styleTag.styleSheet) {
							styleTag.styleSheet.cssText = style;
						} else {
							styleTag.appendChild(document.createTextNode(style));
						}
					}
				}

				if (e.data.type.includes('select')) {
					let temp = e.data.data;

					const { sectionId, currentSectionId, needScroll } = temp;

					if (sectionId) {
						window.dispatchEvent(new CustomEvent('smindDeactivateBlock'));
						window.dispatchEvent(new CustomEvent('smindDeactivateElement'));
						setSelectedSectionId(sectionId);
						window.dispatchEvent(new CustomEvent('smindActivateSection', { detail: sectionId }));

						const activeEl = document.getElementById(sectionId);


						if (activeEl) {
							const mouseEnterEvent = new MouseEvent("mouseenter", {
								bubbles: true,
								cancelable: true,
								view: window
							});

							activeEl.dispatchEvent(mouseEnterEvent);

							const rect = activeEl.getBoundingClientRect();

							if (!!needScroll) {
								const offset = 20;
								const top = activeEl.getBoundingClientRect().top + window.scrollY - offset;

								window.scrollTo({
									top: top,
									behavior: "smooth"
								});
							}
							//}

							const shopifySection = activeEl.querySelector('.shopify-section');
							if (shopifySection) {
								const wrp = shopifySection.parentNode;
								if (wrp) {
									wrp.click();
								}

								if (currentSectionId) {
									shopifySection.dispatchEvent(new CustomEvent('shopify:section:deselect', {
										detail: { sectionId: currentSectionId },
										bubbles: true,
									}));
								}

								shopifySection.dispatchEvent(new CustomEvent('shopify:section:select', {
									detail: { sectionId },
									bubbles: true,
								}));
							}
						}
					}
				}

				if (e.data.type.includes('select-block')) {
					let temp = e.data.data
					const { sectionId, blockId } = temp;

					if (sectionId) {
						setSelectedBlockId({ sectionId, blockId });

						const activeEl = document.getElementById(sectionId);

						if (activeEl) {
							const rect = activeEl.getBoundingClientRect();

							const isPartiallyVisible =
								rect.bottom >= 0 &&
								rect.right >= 0 &&
								rect.top <= (window.innerHeight || document.documentElement.clientHeight) &&
								rect.left <= (window.innerWidth || document.documentElement.clientWidth);

							if (!isPartiallyVisible) {
								activeEl.scrollIntoView({
									behavior: "smooth",
									block: "start",
									inline: "center",
								});
							}

							const shopifySection = activeEl.querySelector('.shopify-section');
							if (shopifySection) {
								const blockEl = shopifySection.querySelector(`[smind-block-id="${blockId}"]`);
								if (blockEl) {
									blockEl.click();
									blockEl.dispatchEvent(new CustomEvent('shopify:block:select', {
										detail: {
											sectionId,
											blockId,
											load: true
										},
										bubbles: true,
									}));
								}
							}
						}
					}
				}

				if (e.data.type.includes('set-inspector-mode')) {
					setInspectorMode(e.data.data?.value);
					const topPreview = document.querySelector('#top-preview');
					if (topPreview) {
						topPreview.setAttribute('data-inspector-active', e.data.data?.value);
					}
				}

				if (e.data.type.includes('trigger-resize')) {
					const forceUpdateSwipers = () => {
						window.dispatchEvent(new Event('resize'));

						document.querySelectorAll('.swiper, .smi-swiper').forEach(el => {
							if (el.swiper && !el.swiper.destroyed) {
								el.swiper.update();

								if (el.swiper.navigation) el.swiper.navigation.update();
								if (el.swiper.pagination) el.swiper.pagination.update();
							}
						});
					};

					requestAnimationFrame(forceUpdateSwipers);

					setTimeout(forceUpdateSwipers, 500);
				}

				if (e.data.type.includes('block-active')) {
					const data = e.data.data;
					const { blockId } = data;

					const blockEl = document.querySelector(`[smind-block-id="${blockId}"]`);
					if (blockEl) {
						blockEl.click();
					}
				}

				if (e.data.type.includes('clear-active-outline')) {
					const topPreviewEl = document.getElementById('top-preview');
					if (topPreviewEl) {
						topPreviewEl.click();
					}
				}

				if (e.data.type.includes('clear-all')) {
					const sections = document.querySelectorAll('section-wrapper');
					sections.forEach(item => item.remove());
					addSections.clear();
				}

				// if (e.data.type.includes('custom-css')) {
				// 	let temp = e.data.data
				// 	const { css } = temp;

				// 	if (css) {
				// 		const styleEl = document.querySelector('#custom-css');
				// 		if (styleEl) {
				// 			styleEl.textContent = css;
				// 		}
				// 	}

				// }

				// if (e.data.type.includes('custom-js')) {
				// 	let temp = e.data.data
				// 	const { javascript } = temp;

				// 	if (javascript !== undefined) {
				// 		try {
				// 			// Remove old script if exists
				// 			const oldScript = document.querySelector('#custom-js');
				// 			if (oldScript) {
				// 				oldScript.remove();
				// 			}

				// 			// Create new script element to execute the code
				// 			const newScript = document.createElement('script');
				// 			newScript.id = 'custom-js';
				// 			newScript.textContent = javascript;
				// 			document.head.appendChild(newScript);
				// 		} catch (error) {
				// 			console.error('Error executing custom JS:', error);
				// 		}
				// 	}

				// }

				if (e.data.type.includes('active-outline')) {
					let temp = e.data.data;
					const { activeId } = temp;

					if (activeId) {
						setSelectedSectionId(false);
						setSelectedBlockId(false);
						setSelectedElementId(false);
						const idArr = activeId.split('__');

						if (idArr.length === 3) {
							let activeElementEl = false;

							//Tìm ra key settings ở vị trí cuối
							//Tìm ra block id
							//Tìm ra section id
							//Đối chiếu sang bên preview
							//Tìm tới section
							//Tìm tới block 
							//Bỏ active section
							//Bỏ active block
							//Tìm các smind-key xem có include cái key không thì gọi active element
							const sectionId = idArr[0];
							const blockId = idArr[1];
							const settingKey = idArr[2];

							const sectionEl = document.querySelector(`#${sectionId}`);
							if (sectionEl) {
								const blockEl = sectionEl.querySelector(`[smind-block-id="${blockId}"]`);
								if (blockEl) {
									const allElemensts = blockEl.querySelectorAll('[smind-key]');
									allElemensts.forEach(el => {
										const keyAttr = el.getAttribute('smind-key');
										if (keyAttr) {
											const keyArr = keyAttr.split(',');
											if (keyArr.includes(settingKey)) {
												activeElementEl = el;
											}
										}
									})
								}
							}

							if (activeElementEl) {
								setSelectedElementId({
									sectionId,
									blockId,
									element: activeElementEl
								});
							} else {
								//Active block								
								

								setTimeout(() => {
									setSelectedElementId(null)
									setSelectedBlockId({
										sectionId,
										blockId
									});
								})
							}
						}

						if (idArr.length === 2) {
							const sectionId = idArr[0];
							const settingKey = idArr[1];
							let activeElementEl = false;

							const sectionEl = document.querySelector(`#${sectionId}`);
							if (sectionEl) {
								const allElemensts = sectionEl.querySelectorAll('[smind-key]');
								allElemensts.forEach(el => {
									const keyAttr = el.getAttribute('smind-key');
									if (keyAttr) {
										const keyArr = keyAttr.split(',');
										if (keyArr.includes(settingKey)) {
											activeElementEl = el;
										}
									}
								})
							}

							if (activeElementEl) {
								setSelectedElementId({
									sectionId,
									blockId: false,
									element: activeElementEl
								});
							} else {
								setSelectedElementId(null);
								setSelectedSectionId(sectionId);
							}
						}
					}
				}
			}
		}

		window.addEventListener('message', handler)
		return () => window.removeEventListener('message', handler)
	}, [origin]);


	useEffect(() => {
		const updateCount = () => {
			setCount(document.querySelectorAll("section-wrapper").length);
		};

		updateCount();

		const observer = new MutationObserver(updateCount);

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});

		return () => observer.disconnect();
	}, []);

	const handleAddTemplate = () => {
		window.parent.postMessage({
			type: ['add-template'],
			data: {
			}
		}, '*');
	}

	//New
	const handleSectionClick = (sectionId) => {
		window.parent.postMessage({
			type: ['setup-section'],
			data: {
				sectionId
			}
		}, '*');
	}

	const handleSectionHide = (sectionId) => {
		window.parent.postMessage({
			type: ['hide-section'],
			data: {
				sectionId
			}
		}, '*');
	}

	const handleSectionRemove = (sectionId) => {
		window.parent.postMessage({
			type: ['delete-section'],
			data: {
				sectionId
			}
		}, '*');
	}

	const handleSectionMoveUp = (sectionId) => {
		window.parent.postMessage({
			type: ['moveup-section'],
			data: {
				sectionId
			}
		}, '*');
	}

	const handleSectionMoveDown = (sectionId) => {
		window.parent.postMessage({
			type: ['movedown-section'],
			data: {
				sectionId
			}
		}, '*');
	}

	const handleSectionAdd = (sectionId, addType, direction) => {
		if (addType === 'section') {
			window.parent.postMessage({
				type: ['add-section'],
				data: {
					sectionId,
					position: direction === 'top' ? 'above' : 'below'
				}
			}, '*');
		} else if (addType === 'template') {
			handleAddTemplate();
		}
	}

	const handleBlockClick = (sectionId, blockId) => {
		window.parent.postMessage({
			type: ['setup-block'],
			data: {
				sectionId,
				blockId
			}
		}, '*');
	}

	const handleBlockHide = (sectionId, blockId) => {
		window.parent.postMessage({
			type: ['hide-block'],
			data: {
				sectionId,
				blockId
			}
		}, '*');
	}

	const handleBlockRemove = (sectionId, blockId) => {
		window.parent.postMessage({
			type: ['delete-block'],
			data: {
				sectionId,
				blockId
			}
		}, '*');
	}

	const handleBlockAdd = (sectionId, blockId, addType, direction) => {
		if (addType === 'block') {
			window.parent.postMessage({
				type: ['add-block'],
				data: {
					sectionId,
					blockId,
					position: direction === 'top' ? 'above' : 'below'
				}
			}, '*');
		}
	}

	const handleElementClick = (sectionId, blockId, settingKey) => {
		window.parent.postMessage({
			type: ['active-element'],
			data: {
				sectionId,
				blockId,
				settingKey
			}
		}, '*');
	}

	const handleTextChange = (data) => {
		const { sectionId, blockId, smindKey, content } = data;

		window.parent.postMessage({
			type: ['input-element'],
			data: {
				sectionId,
				blockId,
				settingType: data?.blockId ? 'block' : 'section',
				settingKey: smindKey,
				settingValue: content
			}
		}, '*');
	}

	return (
		<div className="iframe p-relative" >
			<div className='_iframe-wrapper' ref={previewRef}>
				<div id="top-preview"></div>
			</div>

			{
				!!inspectorMode ? (
					<>
						<OutlineSection
							activeId={selectedSectionId}
							onClick={handleSectionClick}
							onHide={handleSectionHide}
							onRemove={handleSectionRemove}
							onMoveUp={handleSectionMoveUp}
							onMoveDown={handleSectionMoveDown}
							onAdd={handleSectionAdd}
							setSelectedElementId={setSelectedElementId}
							isLimited={count >= 25 ? true : false}
						/>
						<OutlineBlock
							activeId={selectedBlockId}
							onClick={handleBlockClick}
							onSectionClick={handleSectionClick}
							onHide={handleBlockHide}
							onRemove={handleBlockRemove}
							onAdd={handleBlockAdd}
							setSelectedElementId={setSelectedElementId}
						/>
						<OutlineElement
							activeId={selectedElementId}
							onClick={handleElementClick}
							onTextChange={handleTextChange}
						/>
					</>
				) : null
			}

			{/* <div className="add-template" style={{ width: '100%' }}>
				<div className='_inner'>
					<button onClick={handleAddTemplate}>
						<svg class="inline-flex w-5 " viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M3.5 7.25a3.75 3.75 0 0 1 3.75-3.75h5.5a3.75 3.75 0 0 1 3.75 3.75v5.5a3.75 3.75 0 0 1-3.75 3.75h-5.5a3.75 3.75 0 0 1-3.75-3.75v-5.5Zm3.75-2.25a2.25 2.25 0 0 0-2.25 2.25v.5h10v-.5a2.25 2.25 0 0 0-2.25-2.25h-5.5Zm3.75 4.25h-6v3.5a2.25 2.25 0 0 0 2.25 2.25h3.75v-5.75Zm1.5 5.75v-5.75h2.5v3.5a2.25 2.25 0 0 1-2.25 2.25h-.25Z" fill="currentColor"></path></svg>
						<span>Explore templates</span>
					</button>
				</div>
			</div> */}
		</div>
	)
}

export default App;
